import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { FigmaChevronDownIcon } from '../icons';
import './CustomTimePicker.css';

/** Unavailable slots at the start/end of the list (before/after the available span) to keep visible for context. */
const EDGE_UNAVAILABLE_KEPT = 2;

// Convert time string to minutes for comparison
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    return 0;
  }
  return hours * 60 + minutes;
};

// Generate time options (0 and 30 minute increments)
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    const hourStr = hour.toString().padStart(2, '0');
    options.push(`${hourStr}:00`);
    options.push(`${hourStr}:30`);
  }
  return options;
};

/**
 * Drop leading/trailing runs of consecutive unavailable slots to shorten the scroll, but keep
 * EDGE_UNAVAILABLE_KEPT at each end. If total available slots <= maxAvailForFullList, keep the full list.
 */
function collapseEdgeUnavailableSlots(filtered, blockedSet, value, maxAvailForFullList = 3) {
  const n = filtered.length;
  if (n === 0) return filtered;

  const isAvail = (t) => !blockedSet.has(t);
  let firstAvail = -1;
  let lastAvail = -1;
  let availCount = 0;
  for (let i = 0; i < n; i++) {
    if (isAvail(filtered[i])) {
      availCount++;
      if (firstAvail < 0) firstAvail = i;
      lastAvail = i;
    }
  }
  if (firstAvail < 0) return filtered;
  if (availCount <= maxAvailForFullList) return filtered;

  let showMin = firstAvail > EDGE_UNAVAILABLE_KEPT ? firstAvail - EDGE_UNAVAILABLE_KEPT : 0;
  const trailingCount = n - 1 - lastAvail;
  let showMax = trailingCount > EDGE_UNAVAILABLE_KEPT ? lastAvail + EDGE_UNAVAILABLE_KEPT : n - 1;

  if (value) {
    const vi = filtered.indexOf(value);
    if (vi >= 0) {
      showMin = Math.min(showMin, vi);
      showMax = Math.max(showMax, vi);
    }
  }

  return filtered.slice(showMin, showMax + 1);
}

const CustomTimePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select time",
  disabled = false,
  className = "",
  id = "",
  blockedTimes = [], // Array of time strings that should be grayed out (e.g., ['10:00', '10:30', '11:00'])
  minTime = null, // Minimum time that can be selected (for end time picker, should be startTime + 1 hour)
  maxTime = null, // Maximum time that can be selected
  type = 'start', // 'start' or 'end' - determines behavior
  dropdownBelow = false, // When true, always position dropdown below (never flip above)
  /** When true, hide long leading/trailing unavailable runs (keeps EDGE_UNAVAILABLE_KEPT each side); off for full-day pickers */
  collapseEdgeUnavailable = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(value || '');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 'auto' });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const blockedSet = useMemo(() => new Set(blockedTimes || []), [blockedTimes]);

  // Filter and process time options based on blocked times, minTime, maxTime
  const timeOptions = useMemo(() => {
    const allOptions = generateTimeOptions();
    
    const filtered = allOptions.filter(option => {
      const optionMinutes = timeToMinutes(option);
      
      // Filter by minTime if provided
      if (minTime) {
        const minMinutes = timeToMinutes(minTime);
        if (optionMinutes < minMinutes) {
          return false;
        }
      }
      
      // Filter by maxTime if provided
      if (maxTime) {
        const maxMinutes = timeToMinutes(maxTime);
        if (optionMinutes > maxMinutes) {
          return false;
        }
      }
      
      return true;
    });

    if (!collapseEdgeUnavailable || blockedSet.size === 0) {
      return filtered;
    }
    return collapseEdgeUnavailableSlots(filtered, blockedSet, value, 3);
  }, [minTime, maxTime, blockedSet, collapseEdgeUnavailable, value]);

  const isTimeBlocked = (timeStr) => blockedSet.has(timeStr);

  // Handle time selection
  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    if (typeof onChange === 'function') {
      onChange(time);
    }
    setIsOpen(false);
  };

  // Calculate dropdown position when opening
  const calculateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const wrapperRect = inputRef.current.closest('.input-wrapper')?.getBoundingClientRect();
      const rect = wrapperRect || inputRef.current.getBoundingClientRect();
      
      let top = rect.bottom + 4;
      let left = rect.left;
      
      const dropdownHeight = 200;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      
      if (!dropdownBelow && spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        top = rect.top - dropdownHeight - 4;
      }
      
      const dropdownWidth = rect.width;
      const viewportWidth = window.innerWidth;
      const spaceRight = viewportWidth - rect.left;
      
      if (spaceRight < dropdownWidth) {
        left = viewportWidth - dropdownWidth - 10;
      }
      
      if (left < 10) {
        left = 10;
      }
      
      setDropdownPosition({ top, left, width: rect.width });
    }
  }, [dropdownBelow]);

  // Handle opening dropdown
  const handleToggleDropdown = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      if (newIsOpen) {
        setTimeout(() => {
          calculateDropdownPosition();
        }, 0);
      }
    }
  };

  useCloseOnEscape(isOpen, () => setIsOpen(false));

  // Handle click outside & positioning events
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      calculateDropdownPosition();
      
      document.addEventListener('mousedown', handleClickOutside);
      const handleScroll = () => calculateDropdownPosition();
      const handleResize = () => calculateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, calculateDropdownPosition]);

  // Update selected time when value prop changes
  useEffect(() => {
    setSelectedTime(value || '');
  }, [value]);

  return (
    <div className={`custom-time-picker ${className} ${!selectedTime ? 'placeholder' : ''}`}>
      <div 
        ref={inputRef}
        className={`time-picker-input ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggleDropdown}
        id={id}
      >
        <span className="time-display">
          {selectedTime || placeholder}
        </span>
        <FigmaChevronDownIcon className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`} />
      </div>
      
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="time-picker-dropdown"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: typeof dropdownPosition.width === 'number' ? `${dropdownPosition.width}px` : dropdownPosition.width,
            boxSizing: 'border-box',
            zIndex: 10000
          }}
        >
          <div className="time-options-container">
            {timeOptions.map((time) => {
              const isBlocked = isTimeBlocked(time);
              const isDisabled = isBlocked || disabled;
              
              return (
                <div
                  key={time}
                  className={`time-option ${selectedTime === time ? 'selected' : ''} ${isBlocked ? 'blocked' : ''} ${isDisabled ? 'disabled-option' : ''}`}
                  onClick={() => !isDisabled && handleTimeSelect(time)}
                  title={isBlocked ? 'This time is not available' : ''}
                >
                  {time}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTimePicker;